import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  ArrowLeft, Heart, ShoppingCart, Star, Check, 
  Leaf, MapPin, ChevronRight, Minus, Plus 
} from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { useFavorites } from '@/contexts/FavoritesContext';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, getLocalizedField } = useLanguage();
  const { addToCart } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });
  
  const imageScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  useEffect(() => {
    setLoading(true);
    setError(false);
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, retryCount]);

  const handleAddToCart = async () => {
    setIsAdding(true);
    await addToCart(product, quantity);
    setTimeout(() => setIsAdding(false), 1500);
  };

  const handleToggleFavorite = async () => {
    await toggleFavorite(product.id);
  };

  const discount = product?.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <Skeleton className="aspect-square rounded-3xl" />
            <div className="space-y-6">
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-12 w-3/4" />
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center py-32 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Product not found</h2>
          <p className="text-slate-500 mb-8">There was an error loading this product. Please try again.</p>
          <div className="flex gap-4">
            <Button onClick={() => setRetryCount(c => c + 1)} className="btn-primary">
              Retry
            </Button>
            <Button variant="outline" onClick={() => navigate('/')}>
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const benefits = getLocalizedField(product, 'benefits') || [];

  return (
    <div className="min-h-screen bg-background" ref={containerRef}>
      <Navbar />
      
      <main className="pt-24 pb-32" data-testid="product-detail-page">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 text-sm text-slate-500"
          >
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-1 hover:text-primary transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 font-medium">
              {getLocalizedField(product, 'name')}
            </span>
          </motion.nav>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Product Image - Sticky on Desktop */}
            <div className="lg:sticky lg:top-28 lg:self-start">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative aspect-square rounded-3xl overflow-hidden bg-slate-100"
              >
                <motion.img
                  src={product.image}
                  alt={getLocalizedField(product, 'name')}
                  className="w-full h-full object-cover"
                  style={{ scale: imageScale, y: imageY }}
                  data-testid="product-image"
                />
                
                {/* Badges */}
                <div className="absolute top-6 left-6 flex flex-col gap-2">
                  {product.is_on_sale && discount > 0 && (
                    <span className="px-3 py-1.5 rounded-full bg-secondary text-white text-sm font-bold">
                      -{discount}% {t('common.off')}
                    </span>
                  )}
                  {product.is_eco && (
                    <span className="px-3 py-1.5 rounded-full bg-primary text-white text-sm font-bold flex items-center gap-1">
                      <Leaf className="w-4 h-4" />
                      Eco
                    </span>
                  )}
                </div>

                {/* Favorite Button */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleToggleFavorite}
                  className={`absolute top-6 right-6 w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    isFavorite(product.id)
                      ? 'bg-red-500 text-white'
                      : 'bg-white/80 backdrop-blur-sm text-slate-600 hover:bg-white'
                  }`}
                  data-testid="product-favorite-btn"
                >
                  <Heart className={`w-5 h-5 ${isFavorite(product.id) ? 'fill-current' : ''}`} />
                </motion.button>
              </motion.div>
            </div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-8"
            >
              {/* Brand & Name */}
              <div>
                <p className="text-sm font-medium text-primary uppercase tracking-wide mb-2">
                  {product.brand}
                </p>
                <h1 
                  className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight"
                  data-testid="product-name"
                >
                  {getLocalizedField(product, 'name')}
                </h1>
              </div>

              {/* Rating & Reviews */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.floor(product.rating)
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-slate-200'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-lg font-semibold">{product.rating}</span>
                <span className="text-slate-500">
                  ({product.reviews_count} {t('products.reviews')})
                </span>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-4">
                <span className="text-4xl font-bold text-primary" data-testid="product-price">
                  ${product.price.toFixed(2)}
                </span>
                {product.original_price && (
                  <span className="text-xl text-slate-400 line-through">
                    ${product.original_price.toFixed(2)}
                  </span>
                )}
                <span className="text-slate-500">/ {product.unit}</span>
              </div>

              {/* Description */}
              <p className="text-lg text-slate-600 leading-relaxed" data-testid="product-description">
                {getLocalizedField(product, 'description')}
              </p>

              {/* Origin */}
              {product.origin && (
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t('products.origin')}</p>
                    <p className="font-semibold text-slate-900">{product.origin}</p>
                  </div>
                </div>
              )}

              {/* Benefits */}
              {benefits.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    {t('products.benefits')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {benefits.map((benefit, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className="flex items-center gap-3 p-3 rounded-xl bg-primary/5"
                      >
                        <Check className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-slate-700">{benefit}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stock Status */}
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${product.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className={product.stock > 0 ? 'text-green-600' : 'text-red-600'}>
                  {product.stock > 0 ? t('products.inStock') : t('products.outOfStock')}
                </span>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-slate-200">
                {/* Quantity Selector */}
                <div className="flex items-center gap-3 p-2 rounded-2xl bg-slate-100">
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm hover:shadow transition-shadow"
                    data-testid="quantity-decrease"
                  >
                    <Minus className="w-4 h-4" />
                  </motion.button>
                  <span className="w-12 text-center text-xl font-semibold" data-testid="quantity-value">
                    {quantity}
                  </span>
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm hover:shadow transition-shadow"
                    data-testid="quantity-increase"
                  >
                    <Plus className="w-4 h-4" />
                  </motion.button>
                </div>

                {/* Add to Cart Button */}
                <Button
                  onClick={handleAddToCart}
                  disabled={isAdding || product.stock === 0}
                  className={`flex-1 h-14 text-lg font-semibold rounded-2xl transition-all ${
                    isAdding
                      ? 'bg-green-500 hover:bg-green-500'
                      : 'btn-primary'
                  }`}
                  data-testid="add-to-cart-btn"
                >
                  {isAdding ? (
                    <motion.span
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center gap-2"
                    >
                      <Check className="w-5 h-5" />
                      {t('products.addedToCart')}
                    </motion.span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      {t('products.addToCart')} - ${(product.price * quantity).toFixed(2)}
                    </span>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetail;
